import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";

import clsx from "clsx";

import ChevronBottom from "@/design-system/icons/chevron-bottom";
import type { ILink } from "@Components/Link";
import { Link } from "@Components/Link";
import { requestTrack } from "@Components/Modules/Helpers";
import * as stylex from "@stylexjs/stylex";

import { type PropsWithStyleX, colors } from "./../../styles/tokens.stylex";

import * as css from "./ExpandableListItem.module.css";

export interface IExpandableListItem extends PropsWithStyleX {
    linkElement: ILink;
    expandableElement: string;
    id?: string;
    isExpanded?: boolean;
    preventOnItemClick?: boolean;
    colorLinkElement?: string;
    colorExpandableElement?: string;
    isStructuredData?: boolean;
    ajaxTrackAction?: string;
    onExpand?: (isExpanded: boolean) => void;
    arrowStyleX?: stylex.StyleXStyles;
}

export default function ExpandableListItem(props: IExpandableListItem) {
    const [isExpanded, setIsExpanded] = useState(props.isExpanded || false);
    const [height, setHeight] = useState(isExpanded ? 0 : 1);

    const linkElement: ILink = {
        ...props.linkElement,
        onClick: () => {
            setIsExpanded(!isExpanded);

            if (!isExpanded && props.ajaxTrackAction) {
                requestTrack(props.ajaxTrackAction);
            }

            if (props.onExpand) {
                props.onExpand(isExpanded);
            }
            return !props.preventOnItemClick;
        },
        id: props.id,
        initialData: isExpanded,
    };
    const expandedDivRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!expandedDivRef.current) {
            return;
        }

        setHeight(expandedDivRef.current.scrollHeight);
    }, []);

    const expandElementStyle = {
        color: props.colorExpandableElement,
        height: height || undefined,
    } as CSSProperties;

    const mainDivStructured = props.isStructuredData && {
        itemScope: true,
        itemProp: "mainEntity",
        itemType: "https://schema.org/Question",
    };

    const expandedDivStructured = props.isStructuredData && {
        itemScope: true,
        itemProp: "acceptedAnswer",
        itemType: "https://schema.org/Answer",
    };

    return (
        <div
            {...mainDivStructured}
            className={clsx(css.expandableListItemComponent, isExpanded ? css.opened : css.closed)}
        >
            <div className={css.wrapperExpandableItem}>
                <div className={css.linkContainer}>
                    <Link {...linkElement} style={{ color: props.colorLinkElement }}>
                        <p className={css.linkText} itemProp={props.isStructuredData ? "name" : undefined}>
                            {linkElement.text}
                        </p>
                        <span {...stylex.props(styles.arrow, isExpanded && styles.arrowRotate)}>
                            <ChevronBottom />
                        </span>
                    </Link>
                </div>
                <div
                    ref={expandedDivRef}
                    style={expandElementStyle}
                    className={css.expandedContainer}
                    {...expandedDivStructured}
                >
                    <div
                        itemProp={props.isStructuredData ? "text" : undefined}
                        dangerouslySetInnerHTML={{ __html: props.expandableElement }}
                    />
                </div>
            </div>
        </div>
    );
}

const styles = stylex.create({
    arrow: {
        strokeWidth: 2,
        color: colors.b_BrandMagenta,
        flexShrink: 0,
        transitionDuration: "180ms",
        transitionProperty: "transform",
        transitionTimingFunction: "ease-out",
        height: 28,
        width: 28,
    },

    arrowRotate: {
        transform: "rotate(180deg)",
    },
});
